/**
 * usePwaInstall.js
 *
 * Controls the PWA install prompt lifecycle.
 *
 * Rules
 * ─────
 *  1. Only fires in a true PWA environment (browser, NOT already-installed standalone).
 *  2. Apple devices (iOS/iPadOS) are EXCLUDED — IosInstallCoach.vue handles those
 *     with the app's Arabic i18n and the F7 sheet design language.
 *     (@khmyznikov/pwa-install ≥ 0.6.3 added iOS support, so without this gate
 *     both dialogs would fire simultaneously on Apple devices.)
 *  3. Only fires after the user is authenticated (post-login gate).
 *  4. Waits PWA_INSTALL_DELAY ms after login before showing the prompt,
 *     giving the user time to orient themselves in the app first.
 *  5. Respects the user's "dismiss" choice — won't prompt again for
 *     PWA_SNOOZE_DAYS days (stored in localStorage so it survives refreshes).
 *  6. Never over the collector's day (/collector/…): the card slides up from
 *     the bottom, exactly over «خلصت المحل» and each sheet's own action, and it
 *     has no close button — only a drag handle — so it left the collector with
 *     a button that did nothing.
 *
 * Usage
 * ─────
 *   // In a layout component that renders after login:
 *   const { initInstallPrompt } = usePwaInstall()
 *   onMounted(() => initInstallPrompt())
 *
 * The <pwa-install> web component from @khmyznikov/pwa-install must be
 * present in the DOM (we add it lazily).  Its `showDialog()` method is
 * called programmatically — we NEVER rely on the browser's automatic prompt.
 */

import { ref } from 'vue';
import { isCollectorPath } from '@/collector/session.js';
import { useAuthStore } from '@/stores/index.js';

// ── Config constants (adjust freely) ──────────────────────────────────────────
const PWA_INSTALL_DELAY = 5_000;           // ms to wait after login
const PWA_SNOOZE_DAYS   = 7;              // days to suppress after dismiss
const STORAGE_KEY       = 'pwa_install_snoozed_until';
// ──────────────────────────────────────────────────────────────────────────────

const isInstallable = ref(false);   // can the app be installed right now?
let   _element      = null;         // cached reference to <pwa-install> element

/**
 * True when running in a non-Apple browser context where @khmyznikov/pwa-install
 * should handle the install prompt.
 *
 * Apple devices (iPhone/iPad/iPod and iPadOS-as-MacIntel) are excluded because:
 *  - @khmyznikov/pwa-install ≥ 0.6.3 added iOS support, so without this gate
 *    it would show its iOS dialog AND IosInstallCoach.vue simultaneously.
 *  - IosInstallCoach.vue is the designated handler for all Apple devices —
 *    it uses the app's Arabic i18n and the F7 sheet design language.
 */
function isBrowserContext() {
  if (typeof window === 'undefined') return false;
  // Already installed as standalone PWA — no point prompting again.
  const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
  if (isStandalone) return false;

  // Apple devices are handled by IosInstallCoach.vue — exclude them here.
  const ua = navigator.userAgent || '';
  const isApple =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return !isApple;
}

/** True if the user snoozed the prompt and the snooze hasn't expired */
function isSnoozed() {
  try {
    const until = localStorage.getItem(STORAGE_KEY);
    if (!until) return false;
    return Date.now() < Number(until);
  } catch {
    return false;
  }
}

/** Record a snooze so we don't re-prompt for PWA_SNOOZE_DAYS */
function snooze() {
  try {
    const until = Date.now() + PWA_SNOOZE_DAYS * 24 * 60 * 60 * 1_000;
    localStorage.setItem(STORAGE_KEY, String(until));
  } catch { /* storage may be blocked */ }
}

/** Lazily create and mount the <pwa-install> element (once) */
function ensureElement() {
  if (_element) return _element;

  _element = document.getElementById('pwa-install-prompt');
  if (_element) return _element;

  _element = document.createElement('pwa-install');
  _element.id             = 'pwa-install-prompt';
  _element.setAttribute('manifest-url', '/manifest.webmanifest');
  // Suppress the package's own Apple auto-detection: we call showDialog()
  // explicitly after the 5s delay, and IosInstallCoach handles Apple devices.
  _element.setAttribute('manual-apple', '');

  // Listen for the user dismissing the built-in dialog.
  _element.addEventListener('pwa-install-hide-event', () => {
    isInstallable.value = false;
    snooze();
  });

  // Listen for successful install.
  _element.addEventListener('pwa-install-success-event', () => {
    isInstallable.value = false;
  });
  _element.addEventListener('pwa-install-available-event', () => {
    console.log('PWA installation is available');
  });


  _element.addEventListener('pwa-install-fail-event', (event) => {
    console.log('PWA installation failed:', event.detail);
  });

  document.body.appendChild(_element);
  return _element;
}

/**
 * Call this once from any layout/component that renders after login.
 * It is safe to call multiple times — subsequent calls are no-ops.
 *
 * Ordering contract (intentional — do not "fix" by hoisting `_initialized`):
 *   - `_initialized` is set AFTER the auth gate so an early call from a
 *     pre-auth code path does not lock the prompt out for the rest of the
 *     session. The post-auth call from `AppLayout.onMounted` reaches the
 *     auth check, sets the flag, and runs the install flow once.
 *   - Idempotency for re-calls AFTER the prompt has shown is provided by
 *     `_initialized` itself; idempotency for re-calls BEFORE auth resolves
 *     is provided by the auth gate (the function returns early without
 *     mutating state, so the next call retries).
 *   - Callers MUST mount AppLayout (or equivalent) only after `isReady`
 *     flips true in `App.vue`, which is the moment auth resolution is
 *     complete. See PWA audit MEDIUM-14 and the comment on
 *     `AppLayout.onMounted`.
 */
let _initialized = false;

async function initInstallPrompt() {
  // Hard gates — order matters; see contract above.
  if (_initialized)           return;
  if (!isBrowserContext())    return;
  if (isSnoozed())            return;

  // Auth gate — wait until the user is logged in. If we land here pre-auth,
  // bail WITHOUT setting `_initialized` so the next call (post-auth) retries.
  const authStore = useAuthStore();
  if (!authStore.isAuthenticated) return;

  _initialized = true;

  // Wait the configured delay so the user isn't immediately bombarded.
  await new Promise((resolve) => setTimeout(resolve, PWA_INSTALL_DELAY));

  // Re-check snooze after the delay (user might have triggered it elsewhere).
  if (isSnoozed()) return;

  // Rule 6 — asked NOW, not before the delay: the launch handoff calls this
  // while it is still navigating to the collector's day.
  if (isCollectorPath()) return;

  // Dynamically import the web component so it's not in the critical bundle.
  try {
    await import('@khmyznikov/pwa-install');
  } catch (err) {
    console.warn('[PWA Install] Failed to load pwa-install component:', err);
    return;
  }

  const el = ensureElement();
  el.externalPromptEvent = window.promptEvent;
  // Give the browser one tick to upgrade the custom element.
  await new Promise((resolve) => requestAnimationFrame(resolve));

  if (typeof el.showDialog === 'function') {
    isInstallable.value = true;
    el.showDialog(true);   // `true` = show even on desktop
  }
}

/**
 * Manually trigger the install dialog (e.g., from a "Install app" menu item).
 */
async function promptInstall() {
  if (!isBrowserContext()) return;
  const el = ensureElement();
  if (typeof el.showDialog === 'function') {
    el.showDialog(true);
  }
}

export function usePwaInstall() {
  return {
    /** True when the install prompt is visible / available */
    isInstallable,
    initInstallPrompt,
    promptInstall,
  };
}
